'use client'

import React, { useCallback } from 'react'
import { Button } from '../../atoms/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../../atoms/command'
import { Popover } from '../../atoms/popover'
import { mtc } from '@/styles'
import { faCheck } from '@fortawesome/pro-regular-svg-icons'
import {
  faTimes,
  faCaretDown,
  faSpinner,
} from '@fortawesome/pro-solid-svg-icons'
import { Icon } from '@/ui'

export interface ComboboxOption {
  value: string
  label: string
  // optional callback when this item is selected:
  onSelect?: (value: string) => void
  clearable?: boolean
}

export interface ComboboxProps {
  /** the list of available options */
  options: ComboboxOption[]
  /** currently selected value (or `null` / `undefined` for none) */
  value: string | null | undefined
  /** called when the user picks a value (or clears it) */
  onValueChange: (value: string | null) => void
  /** text to show when nothing is selected */
  placeholder?: string
  /** optional className for the trigger button */
  className?: string
  /** width utility for Popover (e.g. `"w-[200px]"`) */
  widthClassName?: string
  /** width utility for Button (e.g. `"w-[200px]"`) */
  buttonWidthClassName?: string
  disabled?: boolean
  /** whether the combobox is clearable */
  clearable?: boolean
  /** callback when input value changes (for server-side search) */
  onInputChange?: (value: string) => void
  /** whether to filter options client-side (default: true, set false for server-side search) */
  shouldFilter?: boolean
  /** callback when scrolled near the end of the list (for infinite scroll) */
  onScrollEnd?: () => void
  /** whether options are currently loading */
  isLoading?: boolean
  /** placeholder text for search input */
  searchPlaceholder?: string
  /** message to show when no results found */
  emptyMessage?: string
  /** whether to hide the caret/clear icons in the trigger */
  hideIcon?: boolean
  /**
   * Set to true when the Combobox lives inside a Radix Dialog. The dropdown
   * then renders as a modal layer *and* portals into the dialog instead of
   * `document.body`. Both halves matter: a dialog's focus trap refuses focus to
   * anything outside its own subtree, so a body-portaled search input can never
   * be typed into no matter how the popover layer is configured.
   */
  modal?: boolean
}

export function Combobox({
  options,
  value,
  onValueChange,
  placeholder = 'Select…',
  className,
  widthClassName = 'min-w-[200px]',
  buttonWidthClassName = 'min-w-[350px]',
  disabled = false,
  clearable = true,
  onInputChange,
  shouldFilter = true,
  onScrollEnd,
  isLoading = false,
  searchPlaceholder = 'Search…',
  emptyMessage = 'No results.',
  hideIcon = false,
  modal = false,
}: ComboboxProps) {
  const [open, setOpen] = React.useState(false)
  const [inputValue, setInputValue] = React.useState('')
  const triggerRef = React.useRef<HTMLButtonElement>(null)

  const dialogContainer =
    modal && open
      ? (triggerRef.current?.closest<HTMLElement>('[role="dialog"]') ??
        undefined)
      : undefined

  const handleInputChange = useCallback(
    (text: string) => {
      setInputValue(text)
      onInputChange?.(text)
    },
    [onInputChange]
  )

  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      if (!onScrollEnd || isLoading) return
      const { scrollTop, scrollHeight, clientHeight } = e.currentTarget
      if (scrollHeight - scrollTop - clientHeight < 50) {
        onScrollEnd()
      }
    },
    [onScrollEnd, isLoading]
  )

  const filtered = React.useMemo(
    () =>
      shouldFilter
        ? options.filter((opt) =>
            opt.label.toLowerCase().includes(inputValue.toLowerCase())
          )
        : options,
    [options, inputValue, shouldFilter]
  )

  // find the label for the current value
  const selectedOption = options.find(
    (opt) => Number(opt.value) === Number(value)
  )
  return (
    <Popover open={open} onOpenChange={setOpen} modal={modal}>
      <Popover.Trigger asChild>
        <Button
          ref={triggerRef}
          variant="pagination"
          role="combobox"
          disabled={disabled}
          aria-expanded={open}
          className={mtc(
            'w-auto inline-flex justify-between gap-1 overflow-hidden',
            buttonWidthClassName,
            className
          )}
        >
          <span
            className={mtc(
              'min-w-0 truncate',
              selectedOption ? 'text-neutral-600' : 'text-neutral-300'
            )}
          >
            {selectedOption?.label ?? placeholder}
          </span>
          {!hideIcon && clearable && value && (
            <button
              type="button"
              aria-label="Clear selection"
              className="ml-2 inline-flex items-center hover:cursor-pointer"
              onPointerDown={(e) => {
                // Radix Popover opens on pointerdown; block it here
                e.preventDefault()
                e.stopPropagation()
              }}
              onClick={(e) => {
                // Also block click bubbling to the trigger
                e.stopPropagation()
                onValueChange(null)
                setOpen(false) // optional: close after clear
              }}
            >
              <Icon icon={faTimes} className="w-4 shrink-0 opacity-50" />
            </button>
          )}
          {!hideIcon && (!value || !clearable) && (
            <Icon icon={faCaretDown} className={'w-4 shrink-0 opacity-50'} />
          )}
        </Button>
      </Popover.Trigger>

      <Popover.Content
        className={mtc(widthClassName, 'p-0')}
        container={dialogContainer}
      >
        <Command shouldFilter={shouldFilter}>
          <div className="relative">
            <CommandInput
              placeholder={searchPlaceholder}
              value={inputValue}
              onValueChange={handleInputChange}
            />
            {isLoading && (
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                <Icon
                  icon={faSpinner}
                  className="w-4 shrink-0 opacity-50 animate-spin"
                />
              </div>
            )}
          </div>
          <CommandList onScroll={handleScroll}>
            <CommandEmpty>{emptyMessage}</CommandEmpty>
            <CommandGroup>
              {filtered.map((opt) => (
                <CommandItem
                  key={String(opt.value)}
                  value={String(opt.value)}
                  keywords={[String(opt.label)]}
                  onSelect={() => {
                    onValueChange(opt.value)
                    opt.onSelect?.(opt.value)
                    setOpen(false)
                    setInputValue('')
                  }}
                  className={mtc(
                    'flex items-center justify-between px-4 py-2 cursor-pointer w-full hover:bg-neutral-100 focus:bg-neutral-100 transition-colors duration-100 ease-in-out'
                  )}
                >
                  <span>{opt.label}</span>
                  {opt.value === value && (
                    <Icon
                      icon={faCheck}
                      className={'w-4 shrink-0 opacity-50'}
                    />
                  )}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </Popover.Content>
    </Popover>
  )
}
